import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { SDatepicker, SFiltersPanel, SSelect } from '@avalara/skylab-sdk/react';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import Logger, { LOG_LEVELS } from '../../../utils/logger';
import LANGUAGES_CONTROLLER, { DEFAULT_LANGUAGE_STRINGS } from '../../../controllers/languages';
import SingleCompanySelector from '../../selectors/company/SingleCompanySelector';
import { FILTERABLE_PROCESSING_STATUSES, getDefaultDateRange, getProcessingStatusLabel, normalizeStatusFilter } from '../../../utils/uploadedDocumentsHelper';

const log = new Logger('UPLOADED_DOCUMENTS_FILTER_PANEL', LOG_LEVELS.DEBUG);

const DATE_DISPLAY_FORMAT = 'DD-MM-YYYY';

// Skylab's expected format which it validates
const DATE_ENTRY_FORMATS = ['DD/MM/YYYY'];

const getDefaultFilters = () => Object.assign({
    companyId: '',
    filename: '',
    manualDocumentsOnly: false,
    status: []
}, getDefaultDateRange());

const getInitialFilters = (appliedFilters = {}) => ({
    companyId: appliedFilters.companyId === undefined || appliedFilters.companyId === null ? '' : `${appliedFilters.companyId}`,
    startDate: appliedFilters.startDate || '',
    endDate: appliedFilters.endDate || '',
    filename: appliedFilters.filename || '',
    manualDocumentsOnly: `${appliedFilters.manualDocumentsOnly}` === 'true',
    status: normalizeStatusFilter(appliedFilters.status)
});

export const UploadedDocumentsFilterPanel = ({
    open,
    shouldRestoreOpen,
    appliedFilters,
    onApply,
    onDismiss,
    onFieldChange,
    languages,
    languageStrings
}) => {
    const panelWrapperRef = useRef(null);
    const statusSelectRef = useRef(null);
    const [selectedFilters, setSelectedFilters] = useState(() => getInitialFilters(appliedFilters));
    const [companySelectorNonce, setCompanySelectorNonce] = useState(0);

    // Every change is reported up so the page can clear the validation banners it owns.
    const updateSelectedFilters = (keyValue) => {
        setSelectedFilters((previous) => Object.assign({}, previous, keyValue));
        onFieldChange(keyValue);
    };

    // Discards an abandoned selection: reopening the panel always shows what is actually applied.
    useEffect(() => {
        if (!open) {
            return;
        }
        setSelectedFilters(getInitialFilters(appliedFilters));
    }, [
        open,
        appliedFilters.companyId,
        appliedFilters.startDate,
        appliedFilters.endDate,
        appliedFilters.filename,
        appliedFilters.manualDocumentsOnly,
        normalizeStatusFilter(appliedFilters.status).join(',')
    ]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const panel = panelWrapperRef.current?.firstElementChild;
        if (!panel) {
            return;
        }
        panel.setAttribute('open', '');
        panel.open = true;
    }, [open, shouldRestoreOpen]);

    // --- Options and labels ----------------------------------------------
    const statusOptions = useMemo(() => FILTERABLE_PROCESSING_STATUSES.map((statusKey) => ({
        value: statusKey,
        label: getProcessingStatusLabel(languages, statusKey) || statusKey,
        selected: selectedFilters.status.includes(statusKey)
    })), [languages, selectedFilters.status]);

    const filtersPanelI18n = useMemo(() => ({
        'filtersPanel.done': LANGUAGES_CONTROLLER.formatUtilFormatter(languageStrings, 'uploadedDocumentsFilterPanel_apply')
    }), [languageStrings]);

    // --- Field handlers --------------------------------------------------
    const dateSelectHandler = (fieldName) => (event) => {
        const selectedValue = event?.detail?.value;
        updateSelectedFilters({ [fieldName]: typeof selectedValue === 'string' ? selectedValue : '' });
    };

    const onStartDateSelect = dateSelectHandler('startDate');
    const onEndDateSelect = dateSelectHandler('endDate');

    const onStatusSelect = (event) => {
        const selectedValue = event?.detail?.item?.value;
        if (!selectedValue) {
            return;
        }
        setSelectedFilters((previous) => previous.status.includes(selectedValue)
            ? previous
            : Object.assign({}, previous, { status: normalizeStatusFilter([...previous.status, selectedValue]) }));
        onFieldChange({ status: selectedValue });
    };

    const onStatusDeselect = (event) => {
        const deselectedValue = event?.detail?.item?.value;
        if (!deselectedValue) {
            return;
        }
        setSelectedFilters((previous) => Object.assign({}, previous, {
            status: previous.status.filter((statusKey) => statusKey !== deselectedValue)
        }));
        onFieldChange({ status: deselectedValue });
    };

    // --- Panel actions ---------------------------------------------------
    const applyFilters = () => {
        log.debug('applyFilters(): selectedFilters: ', selectedFilters);
        onApply({
            companyId: selectedFilters.companyId,
            startDate: selectedFilters.startDate,
            endDate: selectedFilters.endDate,
            filename: selectedFilters.filename.trim(),
            manualDocumentsOnly: selectedFilters.manualDocumentsOnly,
            status: [...selectedFilters.status]
        });
    };

    const applyFiltersOnEnter = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            applyFilters();
        }
    };

    const resetFilters = () => {
        log.debug('resetFilters()');
        const defaultFilters = getDefaultFilters();
        setSelectedFilters(defaultFilters);
        setCompanySelectorNonce((previous) => previous + 1);
        statusSelectRef.current?.deselectAll?.();
        onApply(defaultFilters);
    };

    return (
        <div ref={panelWrapperRef}>
            <SFiltersPanel
                open={open}
                className="mr30"
                data-testid="uploaded-documents-filter-panel"
                i18n={filtersPanelI18n}
                onS-dismiss={onDismiss}
                onS-reset={resetFilters}
                onS-apply={applyFilters}
            >
                <div slot="filters-panel-content">
                    <div className="full-width mb10">
                        <label htmlFor="company-single-select">
                            <LanguageStringResolver string="uploadedDocumentsFilterPanel_company" />
                        </label>
                        <SingleCompanySelector
                            key={`uploaded-documents-company-${companySelectorNonce}`}
                            fieldName="companyId"
                            selectedCompanyId={selectedFilters.companyId}
                            selectionHandler={updateSelectedFilters}
                            selectValueLanguageStringKey="global_All"
                        />
                    </div>
                    <div className="full-width mb10">
                        <label htmlFor="uploaded-documents-start-date">
                            <LanguageStringResolver string="uploadedDocumentsFilterPanel_uploadedAfter" />
                        </label>
                        <SDatepicker
                            inputId="uploaded-documents-start-date"
                            inputName="startDate"
                            data-testid="uploaded-documents-filter-start-date"
                            value={selectedFilters.startDate}
                            primaryFormat={DATE_DISPLAY_FORMAT}
                            alternativeFormats={DATE_ENTRY_FORMATS}
                            onS-select={onStartDateSelect}
                            onS-deselect={onStartDateSelect}
                        />
                    </div>
                    <div className="full-width mb10">
                        <label htmlFor="uploaded-documents-end-date">
                            <LanguageStringResolver string="uploadedDocumentsFilterPanel_uploadedBefore" />
                        </label>
                        <SDatepicker
                            inputId="uploaded-documents-end-date"
                            inputName="endDate"
                            data-testid="uploaded-documents-filter-end-date"
                            value={selectedFilters.endDate}
                            primaryFormat={DATE_DISPLAY_FORMAT}
                            alternativeFormats={DATE_ENTRY_FORMATS}
                            onS-select={onEndDateSelect}
                            onS-deselect={onEndDateSelect}
                        />
                    </div>
                    <div className="full-width mb10">
                        <label htmlFor="uploaded-documents-status-select">
                            <LanguageStringResolver string="uploadedDocuments_fileStatus" />
                        </label>
                        <SSelect
                            ref={statusSelectRef}
                            inputId="uploaded-documents-status-select"
                            name="processStatus"
                            data-testid="uploaded-documents-filter-status"
                            optionsList={statusOptions}
                            multiple={true}
                            showSelectionCount={false}
                            onS-select={onStatusSelect}
                            onS-deselect={onStatusDeselect}
                        />
                    </div>
                    <div className="full-width mb10">
                        <label htmlFor="uploaded-documents-filename-input">
                            <LanguageStringResolver string="uploadedDocumentsFilterPanel_filenameContains" />
                        </label>
                        <input
                            id="uploaded-documents-filename-input"
                            data-testid="uploaded-documents-filter-filename"
                            type="text"
                            value={selectedFilters.filename}
                            onChange={(event) => updateSelectedFilters({ filename: event.target.value })}
                            onKeyDown={applyFiltersOnEnter}
                        />
                    </div>
                    <div className="full-width mb10">
                        <label>
                            <LanguageStringResolver string="uploadedDocumentsFilterPanel_onlyShowManualDocuments" />
                        </label>
                        <input
                            id="uploaded-documents-manual-only-input"
                            data-testid="uploaded-documents-filter-manual-only"
                            type="checkbox"
                            checked={selectedFilters.manualDocumentsOnly}
                            onChange={(event) => updateSelectedFilters({ manualDocumentsOnly: event.target.checked })}
                        />
                        <label className="checkbox pl33" htmlFor="uploaded-documents-manual-only-input">
                            <LanguageStringResolver string="uploadedDocumentsFilterPanel_onlyShowManualDocuments_true" />
                        </label>
                    </div>
                </div>
            </SFiltersPanel>
        </div>
    );
};



UploadedDocumentsFilterPanel.defaultProps = {
    shouldRestoreOpen: false,
    appliedFilters: {},
    onApply: () => { },
    onDismiss: () => { },
    onFieldChange: () => { }
};

function mapStateToProps(state) {
    return {
        languages: state.languages,
        languageStrings: (state.languages && state.languages.data) || DEFAULT_LANGUAGE_STRINGS
    };
}

export default connect(mapStateToProps)(UploadedDocumentsFilterPanel);
